"use client";

import { useGoogleLogin } from "@react-oauth/google";
import { Form, Formik } from "formik";
import Link from "next/link";
import { FC, useState } from "react";
import { appleAuthHelpers, useScript } from "react-apple-signin-auth";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { env } from "@/env";
import { Footer, MediaQuery } from "@/shared/components";
import { Button, Divider, Input, Typography } from "@/shared/ui";
import AppleIcon from "@/svgs/apple.svg";
import EyeIcon from "@/svgs/eye.svg";
import GoogleIcon from "@/svgs/google.svg";

import { useLoginWithApple } from "./hooks/use-login-with-apple.hook";
import { useLoginWithCreds } from "./hooks/use-login-with-creds.hook";
import { useLoginWithGoogle } from "./hooks/use-login-with-google.hook";
import styles from "./login-form.module.scss";
import { SignupFormScheme } from "./login-form.types";

const validationScheme = object({
  email: string().email("Wrong email format").min(1, "Field is required"),
  password: string().min(8, "Must be at least 8 characters."),
});

const LoginForm: FC = () => {
  useScript(appleAuthHelpers.APPLE_SCRIPT_SRC);

  const { mutate: mutateApple, isPending: isLoginWithApplePending } = useLoginWithApple();
  const { mutate: mutateGoogle, isPending: isLoginWithGooglePending } = useLoginWithGoogle();
  const { mutate: mutateCreds, isPending: isLoginWithCredsPending } = useLoginWithCreds();

  const [isPasswordHidden, setIsPasswordHidden] = useState<boolean>(true);

  const googleAuth = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      mutateGoogle({ authorizationCode: code });
    },
    onError: (e) => {
      console.error(e);
    },
  });

  const signupWithGoogleHandler = () => {
    googleAuth();
  };
  const signupWithAppleHandler = () => {
    appleAuthHelpers.signIn({
      authOptions: {
        clientId: env.NEXT_PUBLIC_APPLE_CLIENT_ID,
        redirectURI: env.NEXT_PUBLIC_FRONTEND_URL,
        usePopup: true,
        scope: "email name",
      },
      onSuccess: (data: { authorization: { code: string } }) => {
        mutateApple({ authorizationCode: data.authorization.code });
      },
      onError: (e: any) => {
        console.error(e);
      },
    });
  };
  const onSubmitWithCredentials = (data: SignupFormScheme) => {
    mutateCreds(data);
  };

  return (
    <Formik<SignupFormScheme>
      validateOnBlur={false}
      validateOnChange={false}
      initialValues={{ email: "", password: "" }}
      validationSchema={toFormikValidationSchema(validationScheme)}
      onSubmit={onSubmitWithCredentials}
    >
      {({ values, errors, dirty, isValid, setFieldValue, validateField }) => (
        <Form className={styles.form}>
          <div className={styles.inputs}>
            <Input
              required
              placeholder="Email"
              value={values.email}
              message={errors.email}
              variant={errors.email ? "error" : undefined}
              onBlur={() => validateField("email")}
              onValueChange={async (value) => {
                await setFieldValue("email", value);
                errors.email && (await validateField("email"));
              }}
            />
            <Input
              required
              placeholder="Password"
              value={values.password}
              message={errors.password}
              variant={errors.password ? "error" : undefined}
              type={isPasswordHidden ? "password" : "text"}
              hint={
                <Link href={"/auth/forgot-password"} className={styles.forgotPassword}>
                  <Typography variant="caption-2">Forgot Password</Typography>
                </Link>
              }
              endContent={
                <EyeIcon className={styles.passwordEyeIcon} onClick={() => setIsPasswordHidden((prev) => !prev)} />
              }
              onBlur={() => validateField("password")}
              onValueChange={async (value) => {
                await setFieldValue("password", value);
                errors.password && (await validateField("password"));
              }}
            />
            <Button
              fullWidth
              size="big"
              type="submit"
              disabled={
                isLoginWithCredsPending ||
                isLoginWithGooglePending ||
                isLoginWithApplePending ||
                !dirty ||
                !isValid ||
                Object.values(values).some((v) => !v)
              }
            >
              {isLoginWithCredsPending || isLoginWithApplePending || isLoginWithGooglePending ? "Loading" : "Login"}
            </Button>
          </div>

          <Divider text="Or" />

          <Button size="big" variant="outlined" icon={GoogleIcon} type="button" onClick={signupWithGoogleHandler}>
            Continue with Google
          </Button>
          <Button size="big" variant="outlined" icon={AppleIcon} type="button" onClick={signupWithAppleHandler}>
            Continue with Apple
          </Button>
          <Typography variant="footnote" align="center" className={styles.hintToSignup}>
            Don’t have an account?{" "}
            <Link className={styles.signupLink} href={"/welcome"}>
              Register
            </Link>
          </Typography>

          <MediaQuery.Laptop>
            <Footer fixedAtBottom />
          </MediaQuery.Laptop>
        </Form>
      )}
    </Formik>
  );
};

export { LoginForm };
